import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Campaign, type CampaignStatus } from '../api';
import { AppShell } from '../components/AppShell';

const STATUS_STYLES: Record<CampaignStatus, string> = {
  DRAFT: 'border-white/10 bg-white/5 text-muted',
  ACTIVE: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
  PAUSED: 'border-amber-400/30 bg-amber-400/10 text-amber-300',
  COMPLETED: 'border-white/10 bg-white/5 text-muted',
  EXHAUSTED: 'border-red-400/30 bg-red-400/10 text-red-300',
};

export function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    advertiserName: '',
    contactEmail: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
    priorityWeight: 100,
    impressionCap: 0,
  });

  async function load() {
    try {
      setErr(null);
      setCampaigns(await api.listCampaigns());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.advertiserName.trim()) return;
    try {
      await api.createCampaign({
        advertiserName: form.advertiserName.trim(),
        contactEmail: form.contactEmail.trim() || undefined,
        startDate: form.startDate,
        endDate: form.endDate,
        priorityWeight: Number(form.priorityWeight) || 100,
        impressionCap: Number(form.impressionCap) || undefined,
      });
      setCreating(false);
      setForm((f) => ({ ...f, advertiserName: '', contactEmail: '' }));
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'create_failed');
    }
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
            <p className="mt-1 text-muted">
              Advertiser campaigns and creatives that play on your displays.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setCreating((c) => !c)}
          >
            {creating ? 'Cancel' : '+ New campaign'}
          </button>
        </div>

        {creating && (
          <form className="card space-y-4" onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Advertiser">
                <input
                  className="input"
                  value={form.advertiserName}
                  onChange={(e) =>
                    setForm({ ...form, advertiserName: e.target.value })
                  }
                  required
                />
              </Field>
              <Field label="Contact email (optional)">
                <input
                  className="input"
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) =>
                    setForm({ ...form, contactEmail: e.target.value })
                  }
                />
              </Field>
              <Field label="Start date">
                <input
                  className="input"
                  type="date"
                  value={form.startDate}
                  onChange={(e) =>
                    setForm({ ...form, startDate: e.target.value })
                  }
                  required
                />
              </Field>
              <Field label="End date">
                <input
                  className="input"
                  type="date"
                  value={form.endDate}
                  onChange={(e) =>
                    setForm({ ...form, endDate: e.target.value })
                  }
                  required
                />
              </Field>
              <Field label="Priority weight">
                <input
                  className="input"
                  type="number"
                  min={1}
                  value={form.priorityWeight}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      priorityWeight: Number(e.target.value),
                    })
                  }
                />
              </Field>
              <Field label="Impression cap (0 = no cap)">
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={form.impressionCap}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      impressionCap: Number(e.target.value),
                    })
                  }
                />
              </Field>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary">
                Create campaign
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setCreating(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {err && <p className="card text-red-300">{err}</p>}

        {campaigns.length === 0 ? (
          <p className="card text-muted">
            No campaigns yet. Create one to start serving ads on your displays.
          </p>
        ) : (
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="px-4 py-3 text-left">Advertiser</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Window</th>
                  <th className="px-4 py-3 text-right">Creatives</th>
                  <th className="px-4 py-3 text-right">Impressions</th>
                  <th className="px-4 py-3 text-right">Cap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-white/5">
                    <td className="px-4 py-3">
                      <Link
                        to={`/campaigns/${c.id}`}
                        className="font-semibold text-text hover:text-brand"
                      >
                        {c.advertiserName}
                      </Link>
                      {c.contactEmail && (
                        <div className="text-xs text-muted">
                          {c.contactEmail}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[c.status]}`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted">
                      {c.startDate.slice(0, 10)} → {c.endDate.slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {c.creatives?.length ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {c._count?.impressions ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-muted">
                      {c.impressionCap ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
